import type { Company } from '@vd/types';
import { API_BASE } from '../../lib/apiClient';
import { Mark } from '../projects/ProjectMark';

/**
 * A workspace's mark (TOADO-906): its logo, else a rounded square of its
 * colour with its initials, drawn by the same code as a project's. Shown
 * where someone with many workspaces tells them apart, the switcher first.
 *
 * The logo shows only when the answer says it is an upload
 * (`hasUploadedLogo`): an API from before this change sends the address an
 * owner once typed as `logoUrl`, and that must never become a request from a
 * member's browser. A relative link is the API's (an API with no public base
 * address set), so it is read from the API, which may be on another host than
 * this page (Codex, round 2).
 */
export function WorkspaceMark({ company, size = 16, className }: {
  company: Pick<Company, 'id' | 'name'> & { color?: string; logoUrl?: string | null; hasUploadedLogo?: boolean };
  size?: number;
  className?: string;
}) {
  return (
    <Mark
      name={company.name}
      color={company.color ?? '#64748B'}
      url={company.hasUploadedLogo === true ? logoSource(company.logoUrl) : null}
      size={size}
      className={className}
      testId="workspace-mark"
    />
  );
}

/** An uploaded logo's link, a relative one read from the API's address. */
export function logoSource(url: string | null | undefined, apiBase: string = API_BASE): string | null {
  if (!url) return null;
  return url.startsWith('/') ? `${apiBase}${url}` : url;
}
